<script setup>
import {ref, reactive, computed, watch, toRaw, nextTick, onMounted} from 'vue'
import Tooth from './ToothInput.vue'

const props = defineProps({
    issues: {  type: Array, required: true },
    svgInputName: { type: String, default: 'chart_svg' },
    jsonInputName: { type: String, default: 'chart_json' },
    deciduousInputName: { type: String, default: 'has_deciduous' },
    deciduous: { type: Boolean, default: false },
    data: { required: false, default: [] },
    pngScale: { type: Number, default: 3 }
})

const teeth = {
    top: [
        {id: '18', alignment: 'top'},
        {id: '17', alignment: 'top'},
        {id: '16', alignment: 'top'},
        {id: '15', alignment: 'top'},
        {id: '14', alignment: 'top'},
        {id: '13', alignment: 'top'},
        {id: '12', alignment: 'top'},
        {id: '11', alignment: 'top'},
        {id: '21', alignment: 'top'},
        {id: '22', alignment: 'top'},
        {id: '23', alignment: 'top'},
        {id: '24', alignment: 'top'},
        {id: '25', alignment: 'top'},
        {id: '26', alignment: 'top'},
        {id: '27', alignment: 'top'},
        {id: '28', alignment: 'top'},
    ],
    bottom: [
        {id: '48', alignment: 'bottom'},
        {id: '47', alignment: 'bottom'},
        {id: '46', alignment: 'bottom'},
        {id: '45', alignment: 'bottom'},
        {id: '44', alignment: 'bottom'},
        {id: '43', alignment: 'bottom'},
        {id: '42', alignment: 'bottom'},
        {id: '41', alignment: 'bottom'},
        {id: '31', alignment: 'bottom'},
        {id: '32', alignment: 'bottom'},
        {id: '33', alignment: 'bottom'},
        {id: '34', alignment: 'bottom'},
        {id: '35', alignment: 'bottom'},
        {id: '36', alignment: 'bottom'},
        {id: '37', alignment: 'bottom'},
        {id: '38', alignment: 'bottom'},
    ]
}
const teethTxt = ['8','7','6','5','4','3','2','1','1','2','3','4','5','6','7','8']
const deciduousTeeth = {
    top: [
        {id: '55', alignment: 'top'},
        {id: '54', alignment: 'top'},
        {id: '53', alignment: 'top'},
        {id: '52', alignment: 'top'},
        {id: '51', alignment: 'top'},
        {id: '61', alignment: 'top'},
        {id: '62', alignment: 'top'},
        {id: '63', alignment: 'top'},
        {id: '64', alignment: 'top'},
        {id: '65', alignment: 'top'},
    ],
    bottom: [
        {id: '85', alignment: 'bottom'},
        {id: '84', alignment: 'bottom'},
        {id: '83', alignment: 'bottom'},
        {id: '82', alignment: 'bottom'},
        {id: '81', alignment: 'bottom'},
        {id: '71', alignment: 'bottom'},
        {id: '72', alignment: 'bottom'},
        {id: '73', alignment: 'bottom'},
        {id: '74', alignment: 'bottom'},
        {id: '75', alignment: 'bottom'},
    ]
}
const deciduousTeethTxt = ['5','4','3','2','1','1','2','3','4','5']

const toothSpacing = 50

const svgDataUrl = ref('')
const resultJson = ref('')
const deciduousCheck = ref(props.deciduous)

const searchIssueQuery = ref('')

const result = reactive(props.data)
const current = reactive([])
const svgRoot = ref(null)
const width = computed(() => 16 * (toothSpacing + 2))
const height = computed(() => deciduousCheck.value ? 350 : 195)
const currentFormatted = computed(() => {
    return current.flatMap(item => {
        return Object.entries(item.selected)
            .filter(([_, val]) => val)   // keep only `true`
            .map(([key]) => `${item.key}.${key}`)
    })
})

const resultFormatted = computed(() => {
    return result.flatMap(item => {
        let selectedTooth = item.selected.flatMap(s => {
            return Object.entries(s.selected)
                .map(([key]) => `${s.key}.${key}`)
        })
        return {
            'id': item.id,
            'issue': item.issue,
            'selected': selectedTooth.join(', ')
        };
    })
})

const filteredIssues = computed(() => {
    const q = searchIssueQuery.value.trim().toLowerCase()
    if (!q) return props.issues
    return props.issues.filter(issue => {
        const name = (issue.name || '').toLowerCase()
        const typeText = (issue.type_text || '').toLowerCase()
        return name.includes(q) || typeText.includes(q)
    })
})

function cloneSvgWithInlineStyles(svg) {
    const clone = svg.cloneNode(true)
    const importantProps = [
        'fill', 'fill-opacity', 'stroke', 'stroke-opacity', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'stroke-dashoffset', 'opacity',
        'font-family', 'font-size', 'font-weight', 'font-style', 'text-anchor', 'letter-spacing', 'word-spacing', 'dominant-baseline', 'paint-order', 'vector-effect',
        'shape-rendering'
    ]

    const inlineFor = (el) => {
        if (!(el instanceof Element)) return
        const computed = window.getComputedStyle(el)
        const styles = []
        importantProps.forEach(prop => {
            const val = computed.getPropertyValue(prop)
            if (val) styles.push(`${prop}:${val}`)
        })
        if (styles.length > 0) {
            el.setAttribute('style', `${el.getAttribute('style') || ''};${styles.join(';')}`)
        }
        Array.from(el.children).forEach(child => inlineFor(child))
    }

    inlineFor(clone)
    return clone
}

function handleSelected({ toothId, selected }) {
    const allFalse = Object.values(selected).every(v => v === false)
    const idx = current.findIndex(i => i.key === toothId)

    if (allFalse) {
        if (idx !== -1) current.splice(idx, 1)
    } else {
        const payload = { key: toothId, selected: { ...selected } }

        if (idx !== -1) {
            current[idx] = payload
        } else {
            current.push(payload)
        }
    }
}

function removeAllSelected() {
    current.splice(0);
}

function handleSaveResult(issue) {
    const key = `${issue.value}-${currentFormatted.value.join('-')}`
    const exists = result.find(item => item.id === key)

    if (!exists && current.length > 0) {
        result.push({
            id: key,
            issue: issue,
            selected: [...toRaw(current)]
        })
        removeAllSelected();
    }
}

function plotChart(toothId) {
    let teethChart = [];
    result.forEach(item => {
        const { issue, selected } = item

        selected.forEach(sel => {
            const key = sel.key;
            const flags = sel.selected;

            if (key === toothId) {
                Object.entries(flags).forEach(([flag, isTrue]) => {
                    if (teethChart[flag] === undefined) {
                        teethChart[flag] = {
                            shape: '',
                            text: {
                                content: '',
                                style: '',
                            }
                        }
                    }

                    if (issue.type_value === 'rectangle') {
                        if (flag === 'a') {
                            ['d', 'l', 'm', 'b', 'o'].forEach(part => {
                                if (teethChart[part] === undefined) {
                                    teethChart[part] = {
                                        shape: '',
                                        text: {
                                            content: '',
                                            style: '',
                                        }
                                    }
                                }

                                teethChart[part].shape = issue.color;
                            })
                        } else {
                            teethChart[flag].shape = issue.color;
                        }
                    } else {
                        teethChart[flag].text.content = issue.type_text;
                        teethChart[flag].text.style = issue.color;
                    }
                })
            }
        })
    })

    return teethChart;
}

function removeResult(idx) {
    const index = result.findIndex(item => item.id === idx)
    if (index !== -1) {
        result.splice(index, 1)
    }
}

function handleDeciduousChange(e) {
    deciduousCheck.value = e.target.checked
}

async function updateOutputs() {
    await nextTick()
    const svgEl = svgRoot.value
    if (svgEl) {
    const serializer = new XMLSerializer()
    const svgClone = cloneSvgWithInlineStyles(svgEl)
    const exportWidth = Number(width.value)
    const exportHeight = Number(height.value)
    svgClone.setAttribute('width', String(exportWidth))
    svgClone.setAttribute('height', String(exportHeight))
    const svgString = serializer.serializeToString(svgClone)

        // Render SVG to PNG via offscreen canvas for PDF-safe embedding
        const canvas = document.createElement('canvas')
        const scale = Math.max(window.devicePixelRatio || 1, 1) * (props.pngScale || 1)
        canvas.width = Math.ceil(exportWidth * scale)
        canvas.height = Math.ceil(exportHeight * scale)
        const ctx = canvas.getContext('2d')
        if (ctx) {
            ctx.scale(scale, scale)
            ctx.imageSmoothingEnabled = false
            if ('imageSmoothingQuality' in ctx) {
                // @ts-ignore - not all TS envs have this typed
                ctx.imageSmoothingQuality = 'high'
            }
            // Optional: white background to avoid transparency in PDFs
            ctx.fillStyle = '#ffffff'
            ctx.fillRect(0, 0, exportWidth, exportHeight)
        }

        const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`
        await new Promise((resolve, reject) => {
            const img = new Image()
            img.onload = () => {
                ctx && ctx.drawImage(img, 0, 0, exportWidth, exportHeight)
                try {
                    svgDataUrl.value = canvas.toDataURL('image/png')
                } catch (err) {
                    // Fallback to raw SVG if PNG export fails
                    const base64 = typeof window !== 'undefined'
                        ? window.btoa(unescape(encodeURIComponent(svgString)))
                        : ''
                    svgDataUrl.value = `data:image/svg+xml;base64,${base64}`
                }
                resolve(null)
            }
            img.onerror = (e) => reject(e)
            img.src = svgUrl
        })
    }
    resultJson.value = JSON.stringify(toRaw(result))
}

onMounted(() => {
    updateOutputs()
})

watch([result, current], () => {
    updateOutputs()
}, { deep: true, immediate: true })
</script>

<template>
    <div v-if="currentFormatted.length > 0" class="border border-gray-300 rounded-sm bg-amber-100 p-2 mb-2 flex flex-row justify-between items-center text-xs">
        <div class="mt-1 flex flex-row justify-start items-center gap-1 flex-wrap">
            <span class="font-bold">Selected: </span><span v-for="(c, idx) in currentFormatted" class="uppercase">{{ c }}<span v-if="idx !== currentFormatted.length - 1">, </span></span>
        </div>
        <button class="text-primary" @click="removeAllSelected">
            <i class="ki-duotone ki-cross"></i> Clear
        </button>
    </div>
    <div v-else class="border border-gray-300 rounded-sm bg-info-light p-2 mb-2 text-xs">
        <div class="mt-1 flex flex-row justify-start items-center gap-1 flex-wrap">
            Click a tooth area to edit
        </div>
    </div>
    <div class="flex gap-4">
        <svg ref="svgRoot" :width="width" :height="height" :viewBox="`0 0 ${width} ${height}`" class="border border-gray-300 rounded-sm" :style="`min-width: ${width}`"
             xmlns="http://www.w3.org/2000/svg" version="1.1"
             xmlns:xlink="http://www.w3.org/1999/xlink"
             xmlns:svgjs="http://svgjs.com/svgjs"
             shape-rendering="crispEdges">
            <Tooth
                :arrange="t.alignment"
                v-for="(t, idx) in teeth.top"
                :key="t.id"
                :toothId="t.id"
                :chart="plotChart(t.id)"
                :x="idx * toothSpacing + 20"
                @teeth-selected-area="handleSelected"
                :current="current.find(i => i.key === t.id)?.selected || {}"
            />
            <text text-anchor="middle" v-for="(t, idx) in teethTxt" :x="idx * toothSpacing + 43" :y="101" class="ng-binding ng-scope">{{ t }}</text>
            <Tooth
                :arrange="t.alignment"
                v-for="(t, idx) in teeth.bottom"
                :key="t.id"
                :toothId="t.id"
                :chart="plotChart(t.id)"
                :x="idx * toothSpacing + 20"
                @teeth-selected-area="handleSelected"
                :current="current.find(i => i.key === t.id)?.selected || {}"
            />

            <Tooth
                v-if="deciduousCheck"
                :arrange="t.alignment"
                v-for="(t, idx) in deciduousTeeth.top"
                :key="t.id"
                :toothId="t.id"
                :chart="plotChart(t.id)"
                :x="idx * toothSpacing + 20 + 150"
                :y="165"
                @teeth-selected-area="handleSelected"
                :current="current.find(i => i.key === t.id)?.selected || {}"
            />
            <text v-if="deciduousCheck" text-anchor="middle" v-for="(t, idx) in deciduousTeethTxt" :x="idx * toothSpacing + 43 + 150" :y="264" class="ng-binding ng-scope">{{ t }}</text>
            <Tooth
                v-if="deciduousCheck"
                :arrange="t.alignment"
                v-for="(t, idx) in deciduousTeeth.bottom"
                :key="t.id"
                :toothId="t.id"
                :chart="plotChart(t.id)"
                :x="idx * toothSpacing + 20 + 150"
                :y="159"
                @teeth-selected-area="handleSelected"
                :current="current.find(i => i.key === t.id)?.selected || {}"
            />

        </svg>

        <div class="w-full p-3 border rounded-sm border-gray-300" :style="`height: ${height}px;`">
            <div v-if="resultFormatted.length > 0" class="overflow-y-auto" style="height: 100%">
                <ul class="list-disc list-inside text-xs text-gray-700">
                    <li v-for="(r, idx) in resultFormatted">
                        <div v-if="r.issue.type_value == 'rectangle'" :style="`background: ${r.issue.color}; height: 10px; width: 10px; border-radius: 2px; display: inline-block`"></div>
                        <div v-else :style="`color: ${r.issue.color}; display: inline-block`" class="italic bold">
                            {{ r.issue.type_text }}
                        </div>
                        <strong class="uppercase ml-1">{{ r.selected }}:</strong>
                        <span class="ml-1">{{ r.issue.name }}</span>
                        <a class="link ml-2 hover:underline" style="font-size: 10px; cursor: pointer"  @click="removeResult(r.id)">Remove</a>
                    </li>
                </ul>
            </div>
            <div v-else>
                <p class="text-sm text-gray-500">No items...</p>
            </div>
        </div>
    </div>
    <div class="flex flex-row justify-end items-center">
        <input
            id="chart-deciduous-checkbox"
            type="checkbox"
            :checked="deciduousCheck"
            @change="handleDeciduousChange"
            class="h-3 w-3 text-blue-600 border-gray-300 rounded"
        />
        <label for="chart-deciduous-checkbox" class="ml-1 text-xs text-gray-700">
            Deciduous
        </label>
    </div>
    <div class="flex flex-wrap flex-row items-center gap-2 p-2 border border-gray-300 rounded-sm mt-2 min-h-[50px]">
        <input
            v-model="searchIssueQuery"
            type="text"
            placeholder="Search issues..."
            class="input w-56 input-sm"
        />

        <div v-for="(issue, idx) in filteredIssues" class="btn btn-outline btn-secondary btn-xs border border-gray-500" @click="handleSaveResult(issue)">
            <div v-if="issue.type_value === 'rectangle'" :style="`background: ${issue.color}; height: 10px; width: 10px; border-radius: 2px`"></div>
            <div v-if="issue.type_value !== 'rectangle'" :style="`color: ${issue.color}; font-style: italic; font-weight: bold;`" >
                {{ issue.type_text }}
            </div>
            <span style="margin-left: 3px;">{{ issue.name }}</span>
        </div>
    </div>
    <!-- Hidden inputs for form submission -->
    <input type="hidden" :name="svgInputName" :value="svgDataUrl" />
    <input type="hidden" :name="jsonInputName" :value="resultJson" />
    <input type="hidden" :name="deciduousInputName" :value="deciduousCheck" />
</template>

<style scoped>

</style>
